'use client';

import { HStack } from '@terra/ui-styled-system';
import { Button, MetaDefault, Tooltip } from '@terra/ui-design-system';
import { Plug01Icon, Settings02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type {
  TargetTunnelRibbonState,
  TunnelRequirementEnforcement,
} from '../../hooks/layout/useTargetTunnelRibbonState';

type TunnelRibbonStatusProps = {
  state: TargetTunnelRibbonState;
  isPending: boolean;
  onConnect: () => void;
  onOpenRoutingSettings: () => void;
};

type RibbonLabelColor = 'fg.neutral.muted' | 'fg.warning' | 'fg.error';

const resolveEnforcementColor = (
  enforcement: TunnelRequirementEnforcement,
): RibbonLabelColor => (enforcement === 'block' ? 'fg.error' : 'fg.warning');

function RibbonLabel({
  color = 'fg.neutral.muted',
  children,
}: {
  color?: RibbonLabelColor;
  children: string;
}) {
  return (
    <MetaDefault fontWeight="medium" color={color} whiteSpace="nowrap">
      {children}
    </MetaDefault>
  );
}

export function TunnelRibbonStatus({
  state,
  isPending,
  onConnect,
  onOpenRoutingSettings,
}: TunnelRibbonStatusProps) {
  const t = useTranslations('layout');
  const tCommon = useTranslations('common');
  const tTunnel = useTranslations('settings.internalNetworkTunnel');

  if (state.kind === 'hidden' || state.kind === 'loading') {
    return null;
  }

  if (state.kind === 'error') {
    return (
      <Tooltip title={tTunnel('ribbonRequirementError')}>
        <span>
          <RibbonLabel color="fg.warning">
            {tTunnel('ribbonRequirementError')}
          </RibbonLabel>
        </span>
      </Tooltip>
    );
  }

  if (state.kind === 'idle') {
    const label = state.reconnecting
      ? tCommon('reconnecting')
      : state.connected
        ? t('bottomRibbonTunnelOn')
        : t('bottomRibbonTunnelOff');

    return <RibbonLabel>{label}</RibbonLabel>;
  }

  if (state.kind === 'connectedToTarget') {
    const label = state.reconnecting
      ? tCommon('reconnecting')
      : tTunnel('connectedTo', { network: state.networkName });

    return <RibbonLabel>{label}</RibbonLabel>;
  }

  if (state.kind === 'requiredNotBound') {
    return (
      <HStack gap="1.5" alignItems="center" flexShrink={0}>
        <RibbonLabel color={resolveEnforcementColor(state.enforcement)}>
          {tTunnel('ribbonRequiredNotBound', { network: state.networkName })}
        </RibbonLabel>
        <Button
          variant="outline"
          size="xs"
          h="5"
          px="1.5"
          gap="1"
          disabled={isPending}
          loading={isPending}
          onClick={onConnect}
        >
          <Plug01Icon size={12} />
          {tCommon('connect')}
        </Button>
      </HStack>
    );
  }

  if (state.kind === 'mismatch') {
    return (
      <HStack gap="1.5" alignItems="center" flexShrink={0}>
        <RibbonLabel color={resolveEnforcementColor(state.enforcement)}>
          {tTunnel('ribbonMismatch', { network: state.networkName })}
        </RibbonLabel>
        <Button
          variant="outline"
          size="xs"
          h="5"
          px="1.5"
          gap="1"
          disabled={isPending}
          loading={isPending}
          onClick={onConnect}
        >
          <Plug01Icon size={12} />
          {tTunnel('ribbonReconnect', { network: state.networkName })}
        </Button>
      </HStack>
    );
  }

  if (state.kind === 'routingOff') {
    return (
      <HStack gap="1.5" alignItems="center" flexShrink={0}>
        <RibbonLabel color={resolveEnforcementColor(state.enforcement)}>
          {tTunnel('ribbonRoutingOff', { network: state.networkName })}
        </RibbonLabel>
        <Button
          variant="outline"
          size="xs"
          h="5"
          px="1.5"
          gap="1"
          onClick={onOpenRoutingSettings}
        >
          <Settings02Icon size={12} />
          {tTunnel('ribbonRoutingOffAction')}
        </Button>
      </HStack>
    );
  }

  if (state.kind === 'unscopedHosts') {
    const label = tTunnel('ribbonUnscopedHost', { host: state.primaryHost });

    return (
      <Tooltip title={state.unscopedHosts.join(', ')}>
        <span>
          <RibbonLabel color="fg.warning">{label}</RibbonLabel>
        </span>
      </Tooltip>
    );
  }

  if (state.kind === 'unsupported') {
    const label =
      state.reason === 'twingate'
        ? tTunnel('ribbonUnsupportedTwingate')
        : tTunnel('ribbonUnsupportedNoNetwork');

    return (
      <RibbonLabel color={resolveEnforcementColor(state.enforcement)}>
        {label}
      </RibbonLabel>
    );
  }

  return (
    <RibbonLabel color={resolveEnforcementColor(state.enforcement)}>
      {tTunnel('ribbonNoPermission', { network: state.networkName })}
    </RibbonLabel>
  );
}
